import React, { useRef } from "react";
import { Link } from "react-router-dom";
import "./AnimeCard.css";
import LazyImage from "./LazyImage"; // Import the new LazyImage component
import "./LazyImage.css"; // Import the CSS

const AnimeCard = ({ anime, layout = "horizontal" }) => {
  // Always declare hooks at the top
  const cardRef = useRef(null);

  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (string) => {
    if (!string) return string;
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // Validation checks AFTER all hooks
  if (!anime || typeof anime !== 'object') {
    console.error("Invalid anime object provided to AnimeCard:", anime);
    return <div className="anime-card error-card">Invalid anime data</div>;
  }

  // More flexible ID handling - can be _id, id, or mal_id
  const animeId = anime.id || anime.mal_id || 'unknown';

  console.log("Anime properties:", Object.keys(anime));
  
  // If we don't have an ID at all, still try to render but with a warning
  if (animeId === 'unknown') {
    console.warn("Anime card missing ID property:", anime);
  }

  // Handle null or undefined values
  const score = anime.score ?? 0; // Default to 0 if score is null or undefined
  const episodes = anime.episodes ?? "-";
  const status = anime.status ?? "N/A";
  const type = anime.type ?? "N/A";
  const rank = anime.rank ?? "N/A";
  const popularity = anime.popularity ?? "N/A";
  const season = anime.season ? capitalizeFirstLetter(anime.season) : null; // Capitalize the season
  const year = anime.year ?? "N/A";
  const rating = anime.rating ?? null;

  // Check if anime has an R rating or higher
  const isRRated = rating && (
    rating.includes('R') || 
    rating.includes('Rx') || 
    rating.includes('Mild Nudity')
  );

  // Get image with fallback
  const imageUrl = anime.images?.webp?.large_image_url || 
                   anime.images?.jpg?.large_image_url || 
                   anime.images?.webp?.image_url || 
                   anime.image || 
                   '/default-anime.jpg';

  // Always use horizontal layout
  const cardClass = "anime-card card-horizontal";

  return (
    <div className={cardClass} ref={cardRef}>
      <Link to={`/anime/${animeId}`} className="card-link">
        <div className="card-image-container">
          {/* Replace the img tag with our LazyImage component */}
          <LazyImage 
            src={imageUrl}
            alt={anime.title || "Anime"}
            className="card-image"
            fallbackSrc="/default-anime.jpg"
          />
          <div className="rating-badge">
            ⭐ {score}
          </div>
          
          {/* Simplified R rating badge */}
          {isRRated && (
            <div className="r-badge" title={rating}>
              R
            </div>
          )}
        </div>

        <div className="card-content">
          <div className="metadata-content">
            <h3 className="card-title">{anime.titleEnglish || anime.title || "Unknown Title"}</h3>
            
            <div className="metadata-line">
              <span className="metadata-item">
                <span className="metadata-label">Type:</span> 
                <span className="metadata-value">{type}</span>
              </span>
              <span className="metadata-divider">•</span>
              <span className="metadata-item">
                <span className="metadata-label">Episodes:</span> 
                <span className="metadata-value">{episodes}</span>
              </span>
            </div>

            {/* Wrap this in a div with a mobile-hide class for responsive hiding */}
            <div className="metadata-line mobile-hide">
              <span className="metadata-item">
                <span className="metadata-label">Status:</span> 
                <span className={`metadata-value ${status === 'Currently Airing' ? 'live-status' : ''}`}>{status}</span>
              </span>
              {season && (
                <>
                  <span className="metadata-divider">•</span>
                  <span className="metadata-item">
                    <span className="metadata-label">Season:</span> 
                    <span className="metadata-value">{season} {year}</span>
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="metadata-footer mobile-hide">
            <span className="rank-badge">Rank #{rank}</span>
            <span className="popularity-badge">Popularity #{popularity}</span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default AnimeCard;